import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowForwardRounded, FactCheckOutlined } from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  DocumentCraftingAnswersPayload,
  DocumentCraftingQuestion,
  DocumentCraftingRun,
} from "../../../Models/documentCrafting";
import { useDashboardColors } from "../../HomePage/dashboardColors";
import { Panel } from "../Common/DocumentCraftingUi";
import OutlinePreview from "./OutlinePreview";
import {
  AnswerState,
  AnswersState,
  answeredCount,
  buildAnswersPayload,
  effectiveValue,
  hasValidValue,
  initialAnswers,
  isAnswered,
} from "./runAnswers";

const REMARKS_MAX = 2000;
const ANSWER_MAX = 2000;

interface Props {
  run: DocumentCraftingRun;
  onSubmit: (payload: DocumentCraftingAnswersPayload) => void;
  pending: boolean;
  /** The server's message per question id from a rejected submit
   * (400 `invalid_answers`). */
  errors?: Record<string, string>;
  /** Shown above Continue after a rejected submit. */
  errorMessage?: string | null;
  /** The user changed the answer; its server error no longer applies. */
  onClearError?: (questionId: string) => void;
}

/** Input matching the question's `answer_type`; choices keep a free text
 * "other" answer, because the options are only suggestions. */
const AnswerInput: React.FC<{
  question: DocumentCraftingQuestion;
  answer: AnswerState;
  onChange: (answer: AnswerState) => void;
  disabled: boolean;
}> = ({ question, answer, onChange, disabled }) => {
  const { t } = useTranslation("documentCrafting");
  const options = question.options ?? [];
  const label = t("run.questions.answer");
  const otherField = (help?: string) => (
    <TextField
      fullWidth
      size="small"
      label={t("run.questions.otherAnswer")}
      value={answer.other}
      disabled={disabled}
      onChange={(event) =>
        onChange({
          ...answer,
          other: event.target.value,
          // A free single answer replaces the picked option.
          value: question.answer_type === "single_choice" ? "" : answer.value,
        })
      }
      helperText={help}
      inputProps={{ maxLength: ANSWER_MAX }}
      sx={{ mt: 1 }}
    />
  );

  if (question.answer_type === "single_choice" && options.length) {
    const value = typeof answer.value === "string" ? answer.value : "";
    return (
      <Box>
        <RadioGroup
          value={options.includes(value) ? value : ""}
          onChange={(event) => onChange({ ...answer, value: event.target.value, other: "" })}
        >
          {options.map((option) => (
            <FormControlLabel
              key={option}
              value={option}
              control={<Radio size="small" disabled={disabled} />}
              label={option}
              sx={{ "& .MuiFormControlLabel-label": { fontSize: 14 } }}
            />
          ))}
        </RadioGroup>
        {otherField()}
      </Box>
    );
  }

  if (question.answer_type === "multi_choice" && options.length) {
    const selected = Array.isArray(answer.value) ? answer.value.map(String) : [];
    const toggle = (option: string, checked: boolean) =>
      onChange({
        ...answer,
        value: checked ? [...selected, option] : selected.filter((item) => item !== option),
      });
    return (
      <Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {options.map((option) => (
            <FormControlLabel
              key={option}
              control={
                <Checkbox
                  size="small"
                  disabled={disabled}
                  checked={selected.includes(option)}
                  onChange={(event) => toggle(option, event.target.checked)}
                />
              }
              label={option}
              sx={{ "& .MuiFormControlLabel-label": { fontSize: 14 } }}
            />
          ))}
        </Box>
        {otherField(t("run.questions.otherAnswerHelp"))}
      </Box>
    );
  }

  if (question.answer_type === "number") {
    return (
      <TextField
        fullWidth
        size="small"
        type="number"
        label={label}
        value={typeof answer.value === "number" ? answer.value : ""}
        disabled={disabled}
        onChange={(event) =>
          onChange({
            ...answer,
            value: event.target.value === "" ? null : Number(event.target.value),
          })
        }
      />
    );
  }

  const text = typeof answer.value === "string" ? answer.value : "";
  const invalid =
    !disabled && !!text.trim() && !hasValidValue(question, effectiveValue(question, answer));
  return (
    <TextField
      fullWidth
      size="small"
      type={question.answer_type === "date" ? "date" : question.answer_type === "url" ? "url" : "text"}
      label={label}
      value={text}
      disabled={disabled}
      multiline={question.answer_type === "text"}
      minRows={question.answer_type === "text" ? 2 : undefined}
      placeholder={question.answer_type === "url" ? "https://" : undefined}
      InputLabelProps={question.answer_type === "date" ? { shrink: true } : undefined}
      onChange={(event) => onChange({ ...answer, value: event.target.value })}
      inputProps={{ maxLength: ANSWER_MAX }}
      error={invalid}
      helperText={
        invalid
          ? t(`run.questions.invalid.${question.answer_type}`)
          : question.answer_type === "multi_choice"
            ? t("run.questions.otherAnswerHelp")
            : undefined
      }
    />
  );
};

const QuestionCard: React.FC<{
  question: DocumentCraftingQuestion;
  index: number;
  total: number;
  answer: AnswerState;
  onChange: (answer: AnswerState) => void;
  disabled: boolean;
  error?: string;
}> = ({ question, index, total, answer, onChange, disabled, error }) => {
  const { t } = useTranslation("documentCrafting");
  const { accent } = useDashboardColors();
  const done = isAnswered(question, { [question.id]: answer });
  const borderColor = error ? color.red600 : done ? accent : color.gray200;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "12px",
        border: `1px solid ${color.gray200}`,
        borderLeft: `4px solid ${borderColor}`,
        backgroundColor: color.white,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: color.gray500 }}>
          {t("run.questions.number", { number: index + 1, total })}
        </Typography>
        {question.field_key && (
          <Tooltip title={t("run.questions.fieldDetailHint")} arrow>
            <Chip
              size="small"
              label={t("run.questions.fieldDetail")}
              sx={{ height: 22, fontSize: 11, fontWeight: 700, backgroundColor: color.gray100, color: color.gray700 }}
            />
          </Tooltip>
        )}
        {question.is_fact && !question.field_key && (
          <Tooltip title={t("run.questions.factHint")} arrow>
            <Chip
              size="small"
              icon={<FactCheckOutlined sx={{ fontSize: 14 }} />}
              label={t("run.questions.fact")}
              sx={{ height: 22, fontSize: 11, fontWeight: 700, backgroundColor: color.blue50, color: color.blue700 }}
            />
          </Tooltip>
        )}
      </Box>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: color.gray900 }}>{question.text}</Typography>
      {question.why && (
        <Typography sx={{ fontSize: 13, color: color.gray500, mt: 0.25 }}>{question.why}</Typography>
      )}
      <Box sx={{ mt: 1.5 }}>
        <AnswerInput
          question={question}
          answer={answer}
          disabled={disabled || answer.aiDecides}
          onChange={onChange}
        />
        {error && (
          <Typography
            role="alert"
            data-testid={`question-error-${question.id}`}
            sx={{ mt: 0.75, fontSize: 12, color: color.red600 }}
          >
            {t("run.questions.rejectedAnswer", { message: error })}
          </Typography>
        )}
      </Box>
      <FormControlLabel
        sx={{ mt: 1, ml: 0 }}
        control={
          <Switch
            checked={answer.aiDecides}
            disabled={disabled}
            onChange={(event) => onChange({ ...answer, aiDecides: event.target.checked })}
          />
        }
        label={<Typography sx={{ fontSize: 14, fontWeight: 600 }}>{t("run.questions.aiDecides")}</Typography>}
      />
      {answer.aiDecides && (
        <Typography sx={{ fontSize: 12, color: color.gray500 }}>
          {t(question.is_fact ? "run.questions.aiDecidesFact" : "run.questions.aiDecidesEditorial")}
        </Typography>
      )}
    </Box>
  );
};

/**
 * The run waits for answers: up to five clarifying questions with an input per
 * answer type and "Let the AI decide", the proposed outline (read-only) and
 * remarks on it. Continue is possible once every question is answered or
 * delegated.
 */
const QuestionsStep: React.FC<Props> = ({
  run,
  onSubmit,
  pending,
  errors = {},
  errorMessage,
  onClearError,
}) => {
  const { t } = useTranslation("documentCrafting");
  const questions = run.questions ?? [];
  const [answers, setAnswers] = useState<AnswersState>(() => initialAnswers(questions));
  const [remarks, setRemarks] = useState(run.outline_remarks ?? "");

  const answered = answeredCount(questions, answers);
  const complete = answered === questions.length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {questions.length > 0 && (
        <Panel title={t("run.questions.title")} subtitle={t("run.questions.subtitle")}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {questions.map((question, index) => (
              <QuestionCard
                key={question.id}
                question={question}
                index={index}
                total={questions.length}
                answer={answers[question.id] ?? { value: "", other: "", aiDecides: false }}
                disabled={pending}
                error={errors[question.id]}
                onChange={(answer) => {
                  setAnswers((current) => ({ ...current, [question.id]: answer }));
                  if (errors[question.id]) onClearError?.(question.id);
                }}
              />
            ))}
          </Box>
        </Panel>
      )}

      <Panel title={t("run.outline.title")} subtitle={t("run.outline.subtitle")}>
        <OutlinePreview outline={run.outline} />
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          label={t("run.outline.remarks")}
          placeholder={t("run.outline.remarksPlaceholder")}
          value={remarks}
          disabled={pending}
          onChange={(event) => setRemarks(event.target.value)}
          inputProps={{ maxLength: REMARKS_MAX }}
          helperText={t("run.outline.remarksHelp", { length: remarks.length, max: REMARKS_MAX })}
          sx={{ mt: 2 }}
        />
      </Panel>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
        }}
      >
        <Typography sx={{ fontSize: 13, color: complete ? color.gray500 : color.orange600 }}>
          {questions.length
            ? complete
              ? t("run.questions.allAnswered")
              : t("run.questions.progress", { answered, total: questions.length })
            : t("run.questions.none")}
        </Typography>
        <Button
          variant="contained"
          endIcon={<ArrowForwardRounded />}
          disabled={!complete || pending}
          onClick={() => onSubmit(buildAnswersPayload(questions, answers, remarks))}
        >
          {pending ? t("run.questions.submitting") : t("run.questions.submit")}
        </Button>
      </Box>
    </Box>
  );
};

export default QuestionsStep;
